import { useState } from "react";
import { Link, useParams } from "react-router";
import { AlertCircle, ArrowLeft, ExternalLink, Package, Receipt, Settings } from "lucide-react";
import { useSites } from "../../lib/queries";
import { useOrders, useStore } from "../../lib/store-api";
import { storeCategoryLabel } from "../../lib/store-categories";
import { AppLayout } from "../../components/layout/AppLayout";
import { EmptyState } from "../../components/ui/EmptyState";
import { Spinner } from "../../components/ui/Spinner";
import { ProductsTab } from "./ProductsTab";
import { OrdersTab } from "./OrdersTab";
import { SettingsTab } from "./SettingsTab";

type Tab = "products" | "orders" | "settings";

const TABS: { id: Tab; label: string; icon: typeof Package }[] = [
  { id: "products", label: "Products", icon: Package },
  { id: "orders", label: "Orders", icon: Receipt },
  { id: "settings", label: "Settings", icon: Settings },
];

/** Store management for one store site: products, orders, and settings. */
export function StorePage() {
  const { siteId } = useParams();
  const [tab, setTab] = useState<Tab>("products");
  const { data: sites } = useSites();
  const site = sites?.find((s) => s.id === siteId);
  const store = useStore(siteId);
  const { data: orders } = useOrders(siteId);
  const pendingOrders = orders?.filter((o) => o.status === "pending" || o.status === "paid").length ?? 0;

  if (store.isPending) {
    return (
      <AppLayout>
        <div className="loading-center" style={{ minHeight: "60vh" }}>
          <Spinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (store.error || !store.data || !siteId) {
    return (
      <AppLayout>
        <main className="page page--wide">
          <EmptyState
            icon={<AlertCircle size={28} />}
            title="Store not found"
            description="This site doesn't have an online store, or you don't have access to it."
            action={
              <Link className="btn btn--secondary" to="/app/dashboard">
                <ArrowLeft size={16} />
                Back to dashboard
              </Link>
            }
          />
        </main>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <main className="page page--wide">
        <Link className="store-back" to="/app/dashboard">
          <ArrowLeft size={16} aria-hidden="true" />
          Dashboard
        </Link>
        <header className="store-head">
          <div>
            <span className="eyebrow">{storeCategoryLabel(store.data.category)} store</span>
            <h1>{site?.name ?? "Your store"}</h1>
          </div>
          {site ? (
            <div className="store-head__actions">
              <Link className="btn btn--secondary" to={`/app/sites/${siteId}/editor`}>
                Edit design
              </Link>
              {site.status === "published" ? (
                <a className="btn btn--primary" href={`/s/${site.slug}`} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={16} aria-hidden="true" />
                  View store
                </a>
              ) : (
                <span className="store-head__hint">Publish your site to start selling</span>
              )}
            </div>
          ) : null}
        </header>

        <div className="store-tabs" role="tablist" aria-label="Store sections">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className="store-tab"
                onClick={() => setTab(t.id)}
              >
                <Icon size={16} aria-hidden="true" />
                {t.label}
                {t.id === "orders" && pendingOrders > 0 ? (
                  <span className="store-tab__count" aria-label={`${pendingOrders} to handle`}>
                    {pendingOrders}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div role="tabpanel">
          {tab === "products" ? <ProductsTab siteId={siteId} store={store.data} /> : null}
          {tab === "orders" ? <OrdersTab siteId={siteId} store={store.data} /> : null}
          {tab === "settings" ? <SettingsTab siteId={siteId} store={store.data} /> : null}
        </div>
      </main>
    </AppLayout>
  );
}
